/**
 * Floating cart button + slide-in cart drawer for the public storefront.
 *
 * The button shows a badge with the current item count and toggles the
 * drawer. The drawer lists each cart item with qty steppers and a checkout
 * CTA that navigates to /checkout.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, X, Trash, Plus, Minus, ArrowRight } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function unitInMxn(item, fx) {
  if ((item.currency || "MXN").toUpperCase() === "USD") {
    return Number(item.unit_price || 0) * Number(fx || 17);
  }
  return Number(item.unit_price || 0);
}

export function CartFloatingButton() {
  const { count, setDrawerOpen } = useCart();
  return (
    <button
      type="button"
      onClick={() => setDrawerOpen(true)}
      className="relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white shadow-md transition-colors"
      data-testid="cart-open-btn"
      aria-label="Abrir carrito"
    >
      <ShoppingCart size={18} weight="bold" />
      {count > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#D9A93B] text-zinc-900 text-[11px] font-bold flex items-center justify-center"
          data-testid="cart-count-badge"
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem } = useCart();
  const [fx, setFx] = useState(17);

  useEffect(() => {
    if (!drawerOpen) return;
    axios.get(`${API}/api/public/checkout/config`).then((r) => {
      setFx(Number(r.data?.exchange_rate || 17));
    }).catch(() => {});
  }, [drawerOpen]);

  const subtotal = items.reduce((s, i) => s + unitInMxn(i, fx) * i.qty, 0);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden
      />
      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-white shadow-2xl transition-transform duration-300 flex flex-col ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
        role="dialog"
        aria-label="Carrito de compras"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <h2 className="font-heading text-lg font-extrabold tracking-tight">Tu carrito</h2>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center"
            data-testid="cart-close-btn"
            aria-label="Cerrar"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <ShoppingCart size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
              <p className="text-sm">Tu carrito está vacío.</p>
              <p className="text-xs text-zinc-400 mt-1">Agrega productos desde la tienda.</p>
            </div>
          ) : (
            items.map((it) => {
              const unitMxn = unitInMxn(it, fx);
              const lineMxn = unitMxn * it.qty;
              return (
                <div key={it.product_id} className="flex gap-3 border-b border-zinc-100 pb-4 last:border-0" data-testid={`cart-item-${it.product_id}`}>
                  <div className="w-16 h-16 rounded-md bg-zinc-50 border border-zinc-100 overflow-hidden flex items-center justify-center shrink-0">
                    {it.photo ? (
                      <img src={it.photo} alt={it.name} className="w-full h-full object-contain" loading="lazy" />
                    ) : (
                      <span className="text-[10px] text-zinc-300">Sin foto</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {it.brand && <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono truncate">{it.brand}</div>}
                    <div className="text-sm font-medium leading-tight line-clamp-2">{it.name}</div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="inline-flex items-center bg-zinc-50 border border-zinc-200 rounded-full">
                        <button
                          type="button"
                          onClick={() => updateQty(it.product_id, it.qty - 1)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded-full hover:bg-zinc-100"
                          data-testid={`cart-minus-${it.product_id}`}
                          aria-label="Disminuir"
                        >
                          <Minus size={12} weight="bold" />
                        </button>
                        <span className="w-7 text-center text-xs font-mono font-semibold" data-testid={`cart-qty-${it.product_id}`}>{it.qty}</span>
                        <button
                          type="button"
                          onClick={() => updateQty(it.product_id, it.qty + 1)}
                          disabled={it.qty >= (it.max_qty || 1)}
                          className="w-7 h-7 inline-flex items-center justify-center rounded-full hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`cart-plus-${it.product_id}`}
                          aria-label="Aumentar"
                        >
                          <Plus size={12} weight="bold" />
                        </button>
                      </div>
                      <div className="text-sm font-bold text-zinc-900 numeric" data-testid={`cart-line-${it.product_id}`}>
                        {fmtMXN(lineMxn)} <span className="text-[10px] font-mono text-zinc-400">MXN</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(it.product_id)}
                      className="mt-1 text-[11px] text-zinc-400 hover:text-red-600 inline-flex items-center gap-1"
                      data-testid={`cart-remove-${it.product_id}`}
                    >
                      <Trash size={11} weight="bold" /> Quitar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {items.length > 0 && (
          <footer className="border-t border-zinc-200 px-5 py-4 space-y-3 bg-zinc-50">
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Subtotal</span>
              <span className="font-heading text-2xl font-extrabold text-zinc-900 numeric" data-testid="cart-subtotal">
                {fmtMXN(subtotal)} <span className="text-[10px] font-mono text-zinc-400">MXN</span>
              </span>
            </div>
            <p className="text-[10px] text-zinc-500">El envío (si aplica) se calcula en el siguiente paso.</p>
            <Link
              to="/checkout"
              onClick={() => setDrawerOpen(false)}
              className="block w-full text-center h-12 leading-[3rem] rounded-full bg-zinc-900 hover:bg-zinc-800 text-white font-semibold text-sm transition-colors"
              data-testid="cart-checkout-cta"
            >
              Continuar al pago <ArrowRight size={14} weight="bold" className="inline ml-1" />
            </Link>
          </footer>
        )}
      </aside>
    </>
  );
}
